import React, { useId, useState } from 'react';
import { Check, Copy } from 'lucide-react';

import { Button } from './ui/button';
import { copyText as copyToClipboard } from '../lib/clipboard';
import { cn, EYEBROW } from '../lib/utils';

interface CommandBlockProps {
  /** Uppercase eyebrow describing where the command runs, e.g. "your machine". */
  label?: string;
  /** The command text, verbatim. Newlines are preserved. */
  command: string;
  /**
   * What the copy button puts on the clipboard, when that should differ from
   * what is displayed — e.g. a block shown with `#` comments copies without
   * them, or a snippet shown with its secret masked copies with it. A function
   * is called only at copy time, so a value that must never reach the DOM is
   * built at the moment it is needed. Defaults to `command`.
   */
  copyText?: string | (() => string);
  /**
   * Accessible name for the copy button ("Copy {copyLabel}") and for the
   * scroll region itself, e.g. "install commands".
   */
  copyLabel?: string;
  /**
   * Caps the well's height, e.g. 'max-h-96'. The well then scrolls both ways
   * instead of only sideways — for a snippet taller than its dialog.
   */
  maxHeightClassName?: string;
  /** Replaces the default copy toast, e.g. to state a consequence. */
  copiedMessage?: string;
  /** The noun the copy toasts use ("Snippet copied", "Couldn't copy the snippet"). Defaults to 'Command'. */
  copyToastLabel?: string;
  className?: string;
}

const capitalise = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * A terminal command or code snippet rendered for copying, not for editing.
 *
 * No syntax highlighting: the portal bundles no highlighter, and Monaco is
 * registered for JSON only. A mono well with a copy affordance is the whole
 * treatment — the same shape CredentialField uses for secrets, minus the mask.
 *
 * Long lines scroll inside the well rather than wrapping, so a command stays
 * visually one command, and the page body never scrolls sideways.
 *
 * THE WELL IS A NAMED, FOCUSABLE SCROLL REGION (DESIGN_SYSTEM.md §8.12). It has
 * no focusable children, so without tabIndex a keyboard user could copy a long
 * command but never scroll to read it. It is named from `copyLabel` (what it
 * holds — "Install commands") rather than the eyebrow, because every wizard
 * eyebrow reads "your machine"; the eyebrow becomes its description. The ring
 * is inset so the rounded border never clips it.
 */
export const CommandBlock: React.FC<CommandBlockProps> = ({
  label,
  command,
  copyText,
  copyLabel,
  maxHeightClassName,
  copiedMessage,
  copyToastLabel,
  className,
}) => {
  const [copied, setCopied] = useState(false);
  const labelId = useId();
  const accessibleName = copyLabel ?? label ?? 'command';

  const handleCopy = async () => {
    const text = typeof copyText === 'function' ? copyText() : (copyText ?? command);
    if (
      await copyToClipboard(text, {
        label: copyToastLabel ?? 'Command',
        ...(copiedMessage ? { successMessage: copiedMessage } : {}),
      })
    ) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className={cn('min-w-0', className)}>
      {label ? (
        <p id={labelId} className={cn('mb-1.5', EYEBROW)}>
          {label}
        </p>
      ) : null}
      <div className="relative rounded-md border bg-muted/40">
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          className="absolute right-1 top-1 z-10 shrink-0"
          onClick={handleCopy}
          aria-label={`Copy ${accessibleName}`}
        >
          {copied ? (
            <Check aria-hidden="true" className="text-success-emphasis" />
          ) : (
            <Copy aria-hidden="true" />
          )}
        </Button>
        <pre
          tabIndex={0}
          role="region"
          aria-label={capitalise(accessibleName)}
          aria-describedby={label && label !== accessibleName ? labelId : undefined}
          className={cn(
            maxHeightClassName ? cn('overflow-auto', maxHeightClassName) : 'overflow-x-auto',
            'rounded-md py-3 pl-3 pr-10 font-mono text-xs leading-relaxed text-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
          )}
        >
          {command}
        </pre>
      </div>
    </div>
  );
};
